"use client";

import { useEffect, useRef } from "react";

import { Button } from "./Button";

type Props = {
    title: string;
    body?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    tone?: "default" | "danger";
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * 確認ダイアログのプリミティブ（状態を持たない・`Toast` と同格）。オーバーレイと本体だけを描く。
 * `role="alertdialog"`、Escape でキャンセル・Enter で確定、開いたら確定ボタンへフォーカスし、
 * 簡易フォーカストラップ（Tab をパネル内で循環）を持つ。命令的 API は `confirm()`（confirmStore）。
 */
export function ConfirmDialog({
    title,
    body,
    confirmLabel = "確定",
    cancelLabel = "キャンセル",
    tone = "default",
    onConfirm,
    onCancel,
}: Props) {
    const panelRef = useRef<HTMLDivElement>(null);

    // 開いたら確定ボタン（パネル内の最後の button）へフォーカスする。
    useEffect(() => {
        const buttons = panelRef.current?.querySelectorAll("button");
        buttons?.[buttons.length - 1]?.focus();
    }, []);

    function onKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            onConfirm();
            return;
        }
        if (e.key === "Tab") {
            const buttons = panelRef.current?.querySelectorAll("button");
            if (!buttons || buttons.length === 0) return;
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onKeyDown={onKeyDown}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onCancel();
            }}
        >
            <div
                ref={panelRef}
                role="alertdialog"
                aria-modal="true"
                aria-label={title}
                className="w-full max-w-sm rounded-lg border border-border-subtle bg-surface shadow-lg"
            >
                <div className="px-5 pt-5 pb-4">
                    <p className="text-sm font-semibold text-text-primary">{title}</p>
                    {body && <p className="mt-2 text-sm text-text-muted">{body}</p>}
                </div>
                <div className="flex justify-end gap-2 border-t border-border-subtle px-4 py-3">
                    <Button variant="secondary" size="sm" onClick={onCancel}>
                        {cancelLabel}
                    </Button>
                    <Button
                        variant={tone === "danger" ? "danger" : "primary"}
                        size="sm"
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
}
